import { CaretRightOutlined } from '@ant-design/icons';
import { Input, Select, Tag } from 'antd';
import { ReactNode, useMemo, useState } from 'react';
import { AllReviewsProps, menteeObjectiveProps } from '../../types';
import {
  statusesLabel,
  objectiveStatuses,
  statusColors,
  quarterReviewStatuses,
  Statuses,
} from '../../helper';
import dayjs from 'dayjs';

const { TextArea } = Input;
type usertype = 'mentor' | 'hr' | 'mentee';

interface Props {
  data: menteeObjectiveProps;
  onChange?(data: menteeObjectiveProps): void;
  onFeedbackChange(id: string | undefined, hasValue: boolean): void;
  from: usertype;
  quarter: AllReviewsProps;
}

const Review = ({ data, from, onChange, quarter, onFeedbackChange }: Props) => {
  const [hide, setHide] = useState(true);
  const handleClick = () => setHide(!hide);

  const handleStatusChange = (status: string) =>
    onChange?.({ ...data, status: status });

  // find quarter objective comment object
  const filterObjective = useMemo(() => {
    const find = data.objectiveComments?.findIndex(
      (item) => item.quarterId === quarter.id
    );
    if (find !== undefined && find !== -1)
      return {
        index: find,
        comment: data.objectiveComments?.[find],
      };

    return undefined;
  }, [quarter.id]);

  // find statuses for a quarter objective comment
  const quarterStatus = useMemo(() => {
    const status = quarterReviewStatuses[quarter.status];
    return {
      isClosed: status === Statuses.Completed || status === Statuses.Closed,
      isMenteeSubmitted:
        filterObjective?.comment?.menteeCommentStatus === Statuses.Completed,
      isMentorSubmitted:
        filterObjective?.comment?.mentorCommentStatus === Statuses.Completed,
    };
  }, [quarter.status, filterObjective?.comment]);

  // handle feedback change
  const handleFeedbackChange = (val: string, field: 'mentee' | 'mentor') => {
    if (filterObjective?.index !== undefined && data.objectiveComments) {
      data.objectiveComments[filterObjective?.index][
        field === 'mentor' ? 'mentorComment' : 'menteeComment'
      ] = val;
      return onChange?.(data);
    }
  };

  return (
    <div className="rounded-[8px] p-[10px] bg-[#FAFAFA]">
      <div className="flex cursor-pointer" onClick={handleClick}>
        <CaretRightOutlined
          className={`cursor-pointer ${hide ? '' : 'rotate-90'}`}
        />
        <Label
          isDescription
          title="Objective Description"
          content={data.description}
        />
        <div className="flex ml-auto">
          <div className="flex gap-x-[15px] ml-auto">
            <Label
              title="Set Date"
              content={dayjs(data.startDate).format('MMM DD YYYY')}
            />
            <Label
              title="Target Completion Date"
              content={dayjs(data.endDate).format('MMM DD YYYY')}
            />
            <Label
              disabled={
                quarter.status === 3 ||
                (from === 'mentee' && quarterStatus.isMenteeSubmitted) ||
                (from === 'mentor' && quarterStatus.isMentorSubmitted)
              }
              title="Status"
              content={data.status}
              from={from}
              handleStatusChange={handleStatusChange}
              status={quarter.status}
            />
          </div>
        </div>
      </div>
      <div
        className={`bg-white mt-4 p-[10px] border-b-[#CCD1D2] border-b-[2px] ${hide ? 'hidden' : ''} `}
      >
        <div className="p-[10px]">
          <Label title="Expected Outcome" content={data.expectedOutcome} />
        </div>
        <div
          className={`grid  ${quarterStatus.isClosed || (quarterStatus.isMenteeSubmitted && quarterStatus.isMentorSubmitted) ? 'grid-cols-2' : 'grid-cols-1'} `}
        >
          {from === 'mentee' ||
          quarterStatus.isClosed ||
          (from === 'hr' &&
            !quarterStatus.isClosed &&
            quarterStatus.isMenteeSubmitted) ? (
            <Feedback
              title="Mentee Feedback:"
              value={filterObjective?.comment?.menteeComment || ''}
              noEdit={quarterStatus.isMenteeSubmitted || quarterStatus.isClosed}
              onChange={(val) => {
                handleFeedbackChange(val, 'mentee');
                onFeedbackChange(filterObjective?.index.toString(), val !== '');
              }}
            />
          ) : null}
          {from === 'mentor' ||
          quarterStatus.isClosed ||
          (from === 'hr' &&
            !quarterStatus.isClosed &&
            quarterStatus.isMentorSubmitted) ? (
            <Feedback
              title="Mentor Feedback:"
              value={filterObjective?.comment?.mentorComment || ''}
              noEdit={quarterStatus.isMentorSubmitted || quarterStatus.isClosed}
              onChange={(val) => {
                handleFeedbackChange(val, 'mentor');
                onFeedbackChange(filterObjective?.index.toString(), val !== '');
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Review;

interface LabelProps {
  title: string;
  content: string | ReactNode;
  isDescription?: boolean;
  from?: usertype;
  disabled?: boolean;
  status?: number;
  handleStatusChange?(val: string): void;
}

const Label = ({
  disabled,
  title,
  content,
  isDescription,
  from,
  status,
  handleStatusChange,
}: LabelProps) => {
  if (title === 'Status' && status !== 3 && from === 'mentor') {
    return (
      <div className="flex flex-col">
        <div className="text-[12px] text-[#A6A6A6] text-nowrap">{title}</div>
        <div className="text-[16px] text-nowrap">
          <Select
            disabled={disabled}
            defaultValue={content as string}
            style={{ width: 120 }}
            options={objectiveStatuses}
            onChange={(val: string) => handleStatusChange?.(val)}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="text-[12px] text-[#A6A6A6] text-nowrap">{title}</div>
      <div className={`${isDescription ? 'text-[16px]' : 'text-nowrap'}`}>
        {title === 'Status' ? (
          <Tag color={statusColors[content as string]}>
            {statusesLabel[content as string]}
          </Tag>
        ) : (
          content
        )}
      </div>
    </div>
  );
};

const Feedback = ({
  title,
  value,
  noEdit,
  onChange,
}: {
  title: string;
  value: string;
  noEdit?: boolean;
  onChange(val: string): void;
}) => {
  const [feedback, setFeedback] = useState(value);
  const handleChange = (val: string) => {
    onChange(val);
    setFeedback(val);
  };

  return (
    <div className="py-[15px] px-[10px]">
      <div className="text-[12px] text-[#222222] mb-2">{title}</div>
      {noEdit ? (
        <div className="text-[14px] text-[#000000] px-[10px] py-1">{value}</div>
      ) : (
        <TextArea
          rows={4}
          className="rounded-none text-[14px] text-[#000000] border-[#D9D9D9]"
          onChange={(e) => handleChange(e.target.value)}
          value={feedback}
        >
          {value}
        </TextArea>
      )}
    </div>
  );
};
